import { Suspense, useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersLatestOfferConfiguration } from '@helpers/FormalOffers/getFormalOffersLatestOfferConfiguration';
import { getFormalOffersMaxAvailableCash } from '@helpers/FormalOffers/getFormalOffersMaxAvailableCash';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getTimeOutDate } from '@helpers/FormalOffers/getTimeOutDate';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconUnfoldHorizontal } from '@iwoca/icons';
import {
    fetchGetFormalOffers,
    fetchGetFormalOffersLatestOfferConfiguration,
    fetchPutFormalOffersCreditFacilityConfiguration
} from '@iwoca/lapi-client/edge';
import { Button, Label } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import { ApprovalHeader } from './ApprovalHeader/ApprovalHeader';
import { ComparisonAndRepaymentModal } from './ComparisonAndRepaymentModal/ComparisonAndRepaymentModal';
import { OfferChoiceButtons } from './OfferChoiceButtons/OfferChoiceButtons';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { TrustPilot } from '../../../../TrustPilot/TrustPilot';
import { ChooseAmountInputWithSlider } from '../../../../ui/ChooseAmountInputWithSlider/ChooseAmountInputWithSlider';
import { isSelectedAmountValid } from '../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { getMinAvailableCash } from '../../../MyOffers.helpers';

export const FlexiApprovedView = () => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <MainContentPending data-testid="flexi-approval-view-loading" />
                }
            >
                <FlexiApprovedViewComponent />
            </Suspense>
        </ErrorBoundary>
    );
};

const getSelectedOffer = (
    formalOffers: TFormalOffers,
    preSelectSplitTest: string
) => {
    if (preSelectSplitTest === 'INCLUDE') {
        const longestOffer = _.maxBy(formalOffers, (formalOffer) =>
            getDurationInMonths(formalOffer.duration!)
        );
        if (longestOffer && getDurationInMonths(longestOffer.duration!) >= 24)
            return longestOffer;
    }
    if (formalOffers.length === 1) {
        return formalOffers[0];
    }
    return undefined;
};

const FlexiApprovedViewComponent = () => {
    const [
        comparisonAndRepaymentModalOpen,
        setComparisonAndRepaymentModalOpen
    ] = useState(false);
    const stateKey = useStateKey();

    const { data: getFormalOffersResponse } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        suspense: true
    });

    const formalOffers = getFormalOffersResponse!.data.formal_offers;
    const nonInterestOnlyOffers = formalOffers.filter(
        (offer) => !getIsInterestOnlyOffer(offer)
    );
    const maxCashAmount = getFormalOffersMaxAvailableCash(formalOffers);
    const timeoutDate = getTimeOutDate(formalOffers);

    const { data: latestOfferConfiguration } = useQuery({
        queryKey: ['fetchGetFormalOffersLatestOfferConfiguration', stateKey],
        queryFn: () =>
            fetchGetFormalOffersLatestOfferConfiguration({ stateKey }),
        select: (data) => data.data.latest_offer_configuration,
        suspense: true
    });

    const { previouslySelectedFormalOffer, previouslySelectedAmount } =
        getFormalOffersLatestOfferConfiguration({
            latestOfferConfiguration,
            formalOffers
        });

    const [drawdownAmount, setDrawdownAmount] = useState<number>(
        () => previouslySelectedAmount ?? getMinAvailableCash(formalOffers)
    );

    const preSelectSplitTest = useSplitTestBranch({
        test: 'offer_pre_selection_test_2025_q3',
        suspense: true
    });

    const [selectedOffer, setSelectedOffer] = useState<
        TFormalOffer | undefined
    >(
        () =>
            previouslySelectedFormalOffer ??
            getSelectedOffer(formalOffers, preSelectSplitTest)
    );

    const updateFormalOfferConfiguration = (
        offer: TFormalOffer | undefined,
        amount: number
    ) => {
        if (!offer || !isSelectedAmountValid(amount, offer)) {
            return;
        }

        fetchPutFormalOffersCreditFacilityConfiguration({
            stateKey,
            offerId: offer.offer_id!,
            body: { data: { cash_amount: amount } }
        });
    };

    const updateDrawdownAmount = (amount: number) => {
        setDrawdownAmount(amount);
        updateFormalOfferConfiguration(selectedOffer, amount);
    };

    const hasComparableOffersWithValidAmount =
        nonInterestOnlyOffers.length > 1 &&
        isSelectedAmountValid(drawdownAmount, selectedOffer ?? null);

    const updateSelectedOffer = (offer: TFormalOffer) => {
        tracking.ampli.formalOfferSelected({
            formal_offer_id: offer.offer_id!,
            duration: getDurationInMonths(offer.duration!)
        });
        setSelectedOffer(offer);
        updateFormalOfferConfiguration(offer, drawdownAmount);
    };

    return (
        <div>
            <ApprovalHeader
                approvalStatusTimeOut={timeoutDate}
                maxCashAmount={maxCashAmount}
            />
            <div className="px-l pb-2xl min-sm:px-xl">
                <p className="text-2xl font-bold">
                    <FormattedMessage
                        id="_.flexi_approved.title"
                        defaultMessage="Let’s make your first withdrawal"
                    />
                </p>
                <ChooseAmountInputWithSlider
                    label={
                        <Label htmlFor="choose-amount">
                            <FormattedMessage
                                id="_.flexi_approved.label"
                                defaultMessage="How much would you like to withdraw today?"
                            />
                        </Label>
                    }
                    selectedAmount={drawdownAmount}
                    setSelectedAmount={updateDrawdownAmount}
                    maxAmount={maxCashAmount}
                />

                {formalOffers.length > 1 && (
                    <OfferChoiceButtons
                        formalOffers={formalOffers}
                        selectedOfferId={selectedOffer?.offer_id}
                        drawdownAmount={drawdownAmount}
                        updateSelectedOffer={updateSelectedOffer}
                    />
                )}

                {hasComparableOffersWithValidAmount && (
                    <Button
                        variant="tertiary"
                        className="mt-s"
                        onClick={() => setComparisonAndRepaymentModalOpen(true)}
                    >
                        <IconUnfoldHorizontal />
                        <FormattedMessage
                            id="_.flexi_approved.compare_loans_button"
                            defaultMessage="Compare these options"
                        />
                    </Button>
                )}

                {selectedOffer?.duration && (
                    <div className="mt-xl">
                        {getDurationInMonths(selectedOffer.duration)} months
                    </div>
                )}

                <div className="mt-2xl">
                    <TrustPilot />
                </div>
            </div>

            <ComparisonAndRepaymentModal
                isModalOpen={comparisonAndRepaymentModalOpen}
                onClose={() => setComparisonAndRepaymentModalOpen(false)}
                formalOffers={nonInterestOnlyOffers}
                drawdownAmount={drawdownAmount}
                selectedOffer={selectedOffer!}
            />
        </div>
    );
};
